import { motion } from "framer-motion";
import { useState } from "react";
import type { Position } from "../../types";
import { parseDateString, endOrNow } from "../../lib/dates";
import { employerColorVar } from "../../lib/colors";
import { ITEM_HEIGHT, LANE_HEIGHT, LANE_GAP } from "./constants";
import type { TimelineScales } from "../../hooks/useTimelineScales";

interface PositionBarProps {
  position: Position;
  scales: TimelineScales;
  laneOffsetY: number;
  onHover: (position: Position | null, anchor: { x: number; y: number } | null) => void;
}

export function PositionBar({ position, scales, laneOffsetY, onHover }: PositionBarProps) {
  const [hovered, setHovered] = useState(false);
  const startDate = parseDateString(position.startDate)!;
  const endDate = endOrNow(parseDateString(position.endDate));

  const x = scales.xScale(startDate);
  const pillH = ITEM_HEIGHT - 2;
  const w = Math.max(scales.xScale(endDate) - x, pillH);
  const y =
    laneOffsetY +
    position.subLane * (LANE_HEIGHT + LANE_GAP) +
    (LANE_HEIGHT - ITEM_HEIGHT) / 2;
  const rx = pillH / 2;

  const color = employerColorVar(position.colorIndex);

  const handleEnter = (e: React.MouseEvent) => {
    setHovered(true);
    onHover(position, { x: e.clientX, y: e.clientY });
  };
  const handleLeave = () => {
    setHovered(false);
    onHover(null, null);
  };

  return (
    <motion.g
      initial={{ opacity: 0, y: y + 4 }}
      animate={{ opacity: 1, y: y }}
      transition={{ duration: 0.45, delay: position.subLane * 0.02, ease: [0.22, 0.36, 0.27, 1] }}
      style={{ cursor: "pointer" }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <rect
        x={x}
        y={1}
        width={w}
        height={pillH}
        rx={rx}
        ry={rx}
        fill={color}
        fillOpacity={hovered ? 0.32 : 0.16}
        stroke={color}
        strokeOpacity={hovered ? 1 : 0.85}
        strokeWidth={1.2}
        style={{ transition: "fill-opacity 200ms ease, stroke-opacity 200ms ease" }}
      />
      {/* Label — DM Sans, sized for legibility, truncated if narrow */}
      {w > 60 && (
        <text
          x={x + rx + 4}
          y={pillH / 2 + 4}
          fill="var(--color-cream)"
          fontSize={11}
          fontFamily="var(--font-body)"
          fontWeight={500}
          letterSpacing="-0.005em"
          className="select-none pointer-events-none"
        >
          {truncate(position.title, Math.floor((w - rx * 2) / 6))}
        </text>
      )}
    </motion.g>
  );
}

function truncate(s: string, max: number): string {
  if (max <= 0) return "";
  if (s.length <= max) return s;
  return s.slice(0, Math.max(0, max - 1)) + "…";
}
